import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { QuoteView } from "@contracts/index";
import { labelsFor } from "@presentation/labels";
import { AcceptPanel } from "../../components/AcceptPanel";
import { PortalLayout } from "../../components/PortalLayout";
import { QuoteDocument } from "../../components/QuoteDocument";
import { clientApi, isUnauthorized } from "../../lib/client-api";
import { portalText } from "../../lib/i18n";
import { SignOutButton } from "./MyQuotes";

/** One quote as the client sees it, with the PDF download. */
export function ClientQuote() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [missing, setMissing] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);

  useEffect(() => {
    clientApi
      .getQuote(id)
      .then(setQuote)
      .catch((e: unknown) => {
        if (isUnauthorized(e)) navigate("/", { replace: true });
        else setMissing(true);
      });
  }, [id, navigate]);

  const language = quote?.language ?? "es";
  const text = portalText(language);
  const t = labelsFor(language);

  return (
    <PortalLayout actions={<SignOutButton label={text.signOut} />}>
      <p>
        <Link to="/cotizaciones">{text.back}</Link>
      </p>
      {missing && <p className="error">{text.invalidLink}</p>}
      {!quote && !missing && <p className="muted">{text.loading}</p>}
      {quote && quote.status === "accepted" && (
        <section className="card accepted-box">
          {justAccepted && <p className="notice strong">{t.acceptedThanks}</p>}
          {quote.paymentUrl ? (
            <>
              <a className="button primary" href={quote.paymentUrl} target="_blank" rel="noreferrer">
                {t.payNow}
              </a>
              {justAccepted && <p className="muted small">{t.invoiceSent}</p>}
            </>
          ) : (
            justAccepted && quote.paymentMethod === "transfer" && <p className="muted">{t.transferNext}</p>
          )}
        </section>
      )}
      {quote && (
        <section className="card">
          <div className="actions">
            <a className="button secondary" href={clientApi.pdfUrl(quote.id)}>
              {t.downloadPdf}
            </a>
          </div>
          <QuoteDocument quote={quote} />
        </section>
      )}
      {quote && (quote.status === "sent" || quote.status === "viewed") && !quote.expired && (
        <AcceptPanel
          quote={quote}
          onAccepted={(accepted) => {
            setJustAccepted(true);
            setQuote(accepted);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </PortalLayout>
  );
}
